/**
 * SavingsPage — the cockpit's savings bench.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C05B, adopting the binding
 * cockpit amendment (2026-07-23): savings surfaces use the three-tier
 * taxonomy with explicit denominators, and missing telemetry renders as
 * unavailable, never zero.
 *
 * The three tiers are `SAVINGS_DASHBOARD_SPEC.md`'s, rendered in order and
 * kept visibly separate — they have different denominators and must never
 * be summed into one "savings" number (the exact error PROJECT_BRAIN_SPEC
 * warns about for compaction metrics):
 *
 *   Tier 1 — Provider cache ratio        (`provider_cache_ratio`)
 *   Tier 2 — Input-component savings     (`input_component_savings`)
 *   Tier 3 — Whole-run savings           (`whole_run_savings`)
 *
 * Plus two supporting figures on the same bench: cost per trusted result
 * (`cost_per_trusted_green`) and warm-start delta (`warm_start_delta`).
 *
 * The data dependency is the `GET /v1/evals/savings` route in
 * `auteur_cloud/api/routes/evals.py` (see `api/evals.ts`'s file comment). When
 * the fetch fails (evals unavailable in this deployment, or a missing service),
 * this page renders its honest unavailable state — never a fabricated figure.
 *
 * Build status: compiled and exercised as part of the Vite customer SPA.
 */

import { useEffect, useState } from 'react';
import {
  EvalsApiError,
  fetchSavingsFigures,
  type SavingsFigurePayload,
} from '../../api/evals';
import { MetricCard } from '../../components/Metrics';

/** The three tiers, in display order, with their tier labels. */
const TIER_ORDER: { claimKind: string; tier: string; title: string }[] = [
  {
    claimKind: 'provider_cache_ratio',
    tier: 'Tier 1',
    title: 'Provider cache ratio',
  },
  {
    claimKind: 'input_component_savings',
    tier: 'Tier 2',
    title: 'Input-component savings',
  },
  {
    claimKind: 'whole_run_savings',
    tier: 'Tier 3',
    title: 'Whole-run savings',
  },
];

const SUPPORTING: { claimKind: string; title: string }[] = [
  { claimKind: 'cost_per_trusted_green', title: 'Cost per trusted result' },
  { claimKind: 'warm_start_delta', title: 'Warm-start delta' },
];

export function SavingsPage() {
  const [figures, setFigures] = useState<SavingsFigurePayload[] | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchSavingsFigures()
      .then((result) => {
        if (!cancelled) setFigures(result);
      })
      .catch((error: unknown) => {
        // Evals unavailable in this deployment (EvalsApiError 404/503) or the
        // network failed. Either way: honest unavailable, never a zero.
        if (!cancelled) setUnavailable(true);
        void error;
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const figureFor = (claimKind: string): SavingsFigurePayload | null =>
    (figures || []).find((figure) => figure.claim_kind === claimKind) || null;

  if (unavailable) {
    return (
      <section className="savings-page" data-state="unavailable">
        <h1>Savings</h1>
        <p>
          Unavailable — the savings service is not reachable from this
          deployment yet. No figure on this page is fabricated: every card
          below would render a measured or estimated number with its
          denominator, or an explicit Unavailable state, and nothing else.
        </p>
        <div className="tier-grid">
          {TIER_ORDER.map((tier) => (
            <MetricCard
              key={tier.claimKind}
              title={`${tier.tier} — ${tier.title}`}
              figure={null}
              unavailable
            />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section className="savings-page" data-state={figures ? 'ready' : 'loading'}>
      <h1>Savings</h1>
      <p className="page-note">
        Each tier has its own denominator; the tiers are never summed into one
        number. Missing telemetry renders as Unavailable, never zero.
      </p>
      <div className="tier-grid">
        {TIER_ORDER.map((tier) => (
          <MetricCard
            key={tier.claimKind}
            title={`${tier.tier} — ${tier.title}`}
            figure={figureFor(tier.claimKind)}
          />
        ))}
      </div>
      <div className="supporting-grid">
        {SUPPORTING.map((item) => (
          <MetricCard
            key={item.claimKind}
            title={item.title}
            figure={figureFor(item.claimKind)}
          />
        ))}
      </div>
    </section>
  );
}
